export type Difficulty = 'easy' | 'medium' | 'hard' | 'expert';
export type ChallengeType = 'multiplication' | 'volume' | 'division';

export interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
}

const randInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const shuffle = <T>(arr: T[]): T[] => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const generateWrongAnswers = (correct: number, count: number, min = 1): string[] => {
  const wrongs = new Set<number>();
  const offsets = [1, 2, 3, 5, 8, 10, 12, 15, 20];
  let attempts = 0;
  while (wrongs.size < count && attempts < 100) {
    attempts++;
    const offset = offsets[randInt(0, offsets.length - 1)] * (Math.random() > 0.5 ? 1 : -1);
    const candidate = correct + offset;
    if (candidate !== correct && candidate >= min) wrongs.add(candidate);
  }
  while (wrongs.size < count) {
    const candidate = correct + randInt(1, 20);
    if (candidate !== correct) wrongs.add(candidate);
  }
  return Array.from(wrongs).slice(0, count).map(String);
};

// ─── MULTIPLICATION ──────────────────────────────────────────────────────────
const multiplicationQuestion = (difficulty: Difficulty): Question => {
  let a: number, b: number;
  if (difficulty === 'easy') { a = randInt(2, 5); b = randInt(2, 10); }
  else if (difficulty === 'medium') { a = randInt(6, 12); b = randInt(6, 12); }
  else if (difficulty === 'hard') { a = randInt(12, 20); b = randInt(12, 20); }
  else {
    // Expert: word problems
    const scenarios = [
      () => {
        const shots = randInt(15, 30);
        const games = randInt(8, 16);
        return {
          question: `A tennis player hits ${shots} shots per game and plays ${games} games. How many shots in total?`,
          correct: shots * games,
          explanation: `${shots} × ${games} = ${shots * games}`
        };
      },
      () => {
        const rows = randInt(12, 25);
        const cols = randInt(12, 25);
        return {
          question: `A stadium has ${rows} rows of seats with ${cols} seats in each row. How many seats total?`,
          correct: rows * cols,
          explanation: `${rows} × ${cols} = ${rows * cols}`
        };
      },
      () => {
        const balls = randInt(14, 24);
        const boxes = randInt(14, 24);
        return {
          question: `A sports shop receives ${boxes} boxes, each containing ${balls} tennis balls. How many balls total?`,
          correct: balls * boxes,
          explanation: `${balls} × ${boxes} = ${balls * boxes}`
        };
      },
    ];
    const s = scenarios[randInt(0, scenarios.length - 1)]();
    const options = shuffle([
      String(s.correct),
      ...generateWrongAnswers(s.correct, 3, 1),
    ]);
    return {
      id: crypto.randomUUID(),
      question: s.question,
      options,
      correctAnswer: String(s.correct),
      explanation: s.explanation,
    };
  }
  const correct = a! * b!;
  const options = shuffle([String(correct), ...generateWrongAnswers(correct, 3, 1)]);
  return {
    id: crypto.randomUUID(),
    question: `What is ${a} × ${b}?`,
    options,
    correctAnswer: String(correct),
    explanation: `${a} × ${b} = ${correct}`,
  };
};

// ─── VOLUME ──────────────────────────────────────────────────────────────────
const volumeQuestion = (difficulty: Difficulty): Question => {
  if (difficulty === 'easy') {
    const l = randInt(2, 8), w = randInt(2, 8), h = randInt(2, 8);
    const correct = l * w * h;
    const options = shuffle([String(correct), ...generateWrongAnswers(correct, 3, 1)]);
    return {
      id: crypto.randomUUID(),
      question: `Find the volume of a rectangular prism with length ${l} cm, width ${w} cm, and height ${h} cm.`,
      options,
      correctAnswer: String(correct),
      explanation: `V = l × w × h = ${l} × ${w} × ${h} = ${correct} cm³`,
    };
  }
  if (difficulty === 'medium') {
    const r = randInt(2, 7), h = randInt(3, 10);
    const correct = Math.round(Math.PI * r * r * h);
    const options = shuffle([String(correct), ...generateWrongAnswers(correct, 3, 1)]);
    return {
      id: crypto.randomUUID(),
      question: `A cylinder has radius ${r} cm and height ${h} cm. What is its volume? (Use π ≈ 3.14, round to nearest whole number)`,
      options,
      correctAnswer: String(correct),
      explanation: `V = π × r² × h ≈ 3.14 × ${r}² × ${h} ≈ ${correct} cm³`,
    };
  }
  if (difficulty === 'hard') {
    const type = randInt(0, 1);
    if (type === 0) {
      const r = randInt(2, 6), h = randInt(4, 12);
      const correct = Math.round((1 / 3) * Math.PI * r * r * h);
      const options = shuffle([String(correct), ...generateWrongAnswers(correct, 3, 1)]);
      return {
        id: crypto.randomUUID(),
        question: `Find the volume of a cone with radius ${r} cm and height ${h} cm. (Use π ≈ 3.14, round to whole number)`,
        options,
        correctAnswer: String(correct),
        explanation: `V = (1/3) × π × r² × h ≈ (1/3) × 3.14 × ${r}² × ${h} ≈ ${correct} cm³`,
      };
    } else {
      const r = randInt(2, 6);
      const correct = Math.round((4 / 3) * Math.PI * r * r * r);
      const options = shuffle([String(correct), ...generateWrongAnswers(correct, 3, 1)]);
      return {
        id: crypto.randomUUID(),
        question: `Find the volume of a sphere with radius ${r} cm. (Use π ≈ 3.14, round to whole number)`,
        options,
        correctAnswer: String(correct),
        explanation: `V = (4/3) × π × r³ ≈ (4/3) × 3.14 × ${r}³ ≈ ${correct} cm³`,
      };
    }
  }
  // Expert
  const l = randInt(5, 12), w = randInt(3, 8), h = randInt(4, 10);
  const lr = randInt(1, 2), lh = randInt(2, 4);
  const mainVol = l * w * h;
  const holeVol = Math.round(Math.PI * lr * lr * lh);
  const correct = mainVol - holeVol;
  const options = shuffle([String(correct), ...generateWrongAnswers(correct, 3, 1)]);
  return {
    id: crypto.randomUUID(),
    question: `A rectangular box (${l}×${w}×${h} cm) has a cylindrical hole (r=${lr} cm, h=${lh} cm) drilled through it. What is the remaining volume? (Use π≈3.14, round to whole number)`,
    options,
    correctAnswer: String(correct),
    explanation: `Box: ${l}×${w}×${h}=${mainVol} cm³, Hole: π×${lr}²×${lh}≈${holeVol} cm³, Remaining: ${mainVol}-${holeVol}=${correct} cm³`,
  };
};

// ─── DIVISION ────────────────────────────────────────────────────────────────
const divisionQuestion = (difficulty: Difficulty): Question => {
  if (difficulty === 'easy') {
    const b = randInt(2, 10);
    const result = randInt(2, 12);
    const a = b * result;
    const options = shuffle([String(result), ...generateWrongAnswers(result, 3, 1)]);
    return {
      id: crypto.randomUUID(),
      question: `What is ${a} ÷ ${b}?`,
      options,
      correctAnswer: String(result),
      explanation: `${a} ÷ ${b} = ${result}`,
    };
  }
  if (difficulty === 'medium') {
    const b = randInt(3, 9);
    const a = randInt(20, 99);
    const result = Math.floor(a / b);
    const remainder = a % b;
    const correct = remainder === 0 ? String(result) : `${result} R${remainder}`;
    const wrongs: string[] = [];
    const altResults = [result - 1, result + 1, result + 2];
    altResults.forEach(r => {
      if (r > 0 && String(r) !== correct) {
        const altR = randInt(0, b - 1);
        wrongs.push(altR === 0 ? String(r) : `${r} R${altR}`);
      }
    });
    while (wrongs.length < 3) wrongs.push(`${result + wrongs.length + 1} R${randInt(1, b - 1)}`);
    const options = shuffle([correct, ...wrongs.slice(0, 3)]);
    return {
      id: crypto.randomUUID(),
      question: `What is ${a} ÷ ${b}? (Express remainder as R if any)`,
      options,
      correctAnswer: correct,
      explanation: `${a} ÷ ${b} = ${result}${remainder !== 0 ? ` remainder ${remainder}` : ''} → ${correct}`,
    };
  }
  if (difficulty === 'hard') {
    const b = randInt(6, 15);
    const a = randInt(100, 999);
    const result = (a / b).toFixed(1);
    const options = shuffle([result, ...[(a / b + 0.5).toFixed(1), (a / b - 0.5).toFixed(1), (a / b + 1.2).toFixed(1)]]);
    return {
      id: crypto.randomUUID(),
      question: `Calculate ${a} ÷ ${b} (round to 1 decimal place)`,
      options,
      correctAnswer: result,
      explanation: `${a} ÷ ${b} = ${result}`,
    };
  }
  // Expert: word problem
  const balls = randInt(120, 500);
  const players = randInt(8, 20);
  const result = Math.floor(balls / players);
  const rem = balls % players;
  const correct = String(result);
  const options = shuffle([correct, ...generateWrongAnswers(result, 3, 1)]);
  return {
    id: crypto.randomUUID(),
    question: `A tournament distributes ${balls} tennis balls equally among ${players} players. How many does each player receive? (Ignore any remainder)`,
    options,
    correctAnswer: correct,
    explanation: `${balls} ÷ ${players} = ${result} remainder ${rem}. Each player gets ${result} balls.`,
  };
};

export const generateQuestion = (type: ChallengeType, difficulty: Difficulty): Question => {
  switch (type) {
    case 'multiplication': return multiplicationQuestion(difficulty);
    case 'volume': return volumeQuestion(difficulty);
    case 'division': return divisionQuestion(difficulty);
  }
};
